import { Injectable, inject, signal } from '@angular/core';
import { Router }                    from '@angular/router';
import { BsModalService }            from 'ngx-bootstrap/modal';
import { forkJoin }                  from 'rxjs';
import { TranslateService }          from '@ngx-translate/core';
import posthog                       from 'posthog-js';

import { HttpService }              from 'src/app/services/http.service';
import { FonctionsService }         from 'src/app/services/fonctions-partagees.service';
import { UtilisateurService }       from 'src/app/services/utilisateur.service';
import { SiteNavigationService }    from 'src/app/services/new_services/site-navigation.service';
import { Tir }                      from 'src/app/bo/tir';
import { Zone }                     from 'src/app/bo/zone';
import { TypeTir }                  from 'src/app/bo/TypeTir';
import { Projet }                   from 'src/app/bo/projet';
import { Fichier }                  from 'src/app/bo/fichier';
import { TirModaleComponent }       from 'src/app/components/tir-modale/tir-modale.component';
import { RapportCreationModaleComponent } from 'src/app/components/rapport-creation-modale/rapport-creation-modale.component';

/**
 * Pont de transition : création d'un blast (bouton « Blast » de la treeview) et actions
 * « Report » / « Modify » de la page-bar d'un blast.
 *
 * Reprise de TreeviewComponent.openModalcreateTir et de TirInfoComponent (creerRapport /
 * ouvrirModaleModif) : mêmes modales de l'ancien front, mêmes appels API. Les modales étant rendues dans <body> (.new-theme sur /new-site),
 * `old-theme` est posé sur la .modal-dialog pour que styles.scss s'applique à leur contenu.
 *
 * Suppose LegacySiteContextService.ready() : les modales lisent `currentSite()` sans garde.
 *
 * À supprimer quand ces modales auront été refondues.
 */
@Injectable({ providedIn: 'root' })
export class LegacyBlastActionsService {

  private readonly http             = inject(HttpService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly userService      = inject(UtilisateurService);
  private readonly modalService     = inject(BsModalService);
  private readonly router           = inject(Router);
  private readonly siteNav          = inject(SiteNavigationService);
  private readonly translate        = inject(TranslateService);

  /**
   * Reprise de TreeviewComponent.openModalcreateTir.
   *
   * L'ancienne treeview n'affichait le bouton que dans un projet ouvert. Ici le bouton est toujours
   * disponible : le projet proposé est celui sélectionné dans l'arbre s'il est ouvert, sinon le projet
   * ouvert le plus récent du site. La modale permet ensuite d'en choisir un autre.
   */
  openCreate(preferredProjetId: number | null): void {
    const siteId = this.userService.currentSite()?.siteId;
    posthog.capture('openModalcreateTir', { siteId });
    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);
    const stopLoader  = () => {
      clearTimeout(startLoader);
      this.fonctionsService.loading_screen(false);
    };
    const onError = () => {
      stopLoader();
      this.translate.get('GLOBAL.ERREUR_RECUP_DONNEES').subscribe(trad => {
        this.fonctionsService.launch_toast(false, false, trad);
      });
    };

    forkJoin([
      this.http.get<Zone[]>('Sites/' + siteId + '/Zones'),
      this.http.get<TypeTir[]>('Sites/' + siteId + '/BlastTypes'),
      this.http.get<Projet[]>('Sites/' + siteId + '/Projects'),
    ]).subscribe({
      next: ([zones, typeTirs, projets]) => {
        const projetsActifs = projets.filter(p => p.estTermine == false);
        const projetId = this.pickProjetId(projetsActifs, preferredProjetId);
        if (projetId === null) {
          stopLoader();
          this.fonctionsService.launch_toast(false, false, 'No active project on this site.');
          return;
        }

        // Projet complet (avec ses tirs) : TirModaleComponent lit currentProjet dès son
        // constructeur, et le nom proposé dépend des tirs existants.
        this.http.get<Projet>('Projects/' + projetId).subscribe({
          next: projet => {
            stopLoader();
            this.userService.currentProjet = projet;

            const modalRef = this.modalService.show(TirModaleComponent, {
              backdrop:            true,
              ignoreBackdropClick: true,
              keyboard:            false,
              class:               'modal-lg modal-dialog-centered old-theme',
              animated:            false,
              initialState: {
                tabZones:    zones.filter(o => o.isActif),
                tabTypeTirs: typeTirs,
                nomTir:      this.nextBlastName(projet),
                projetId:    projet.projetId,
                estModif:    false,
                tabProjets:  projetsActifs,
              } as Partial<TirModaleComponent>,
            });

            modalRef.content!.modifTir.subscribe(tir => this.onBlastCreated(tir, projetId));
          },
          error: onError,
        });
      },
      error: onError,
    });
  }

  /** Projet sélectionné s'il est ouvert, sinon le projet ouvert au début prévu le plus récent. */
  private pickProjetId(projetsActifs: Projet[], preferredProjetId: number | null): number | null {
    if (preferredProjetId !== null && projetsActifs.some(p => p.projetId === preferredProjetId)) {
      return preferredProjetId;
    }
    const time = (p: Projet) => p.dateDebutPrevisionnelle ? new Date(p.dateDebutPrevisionnelle).getTime() : 0;
    const plusRecent = [...projetsActifs].sort((a, b) => time(b) - time(a))[0];
    return plusRecent?.projetId ?? null;
  }

  /** Reprise du calcul de TreeviewComponent : « <projet>_<n+1> ». */
  private nextBlastName(projet: Projet): string {
    const prefixe = projet.nom + '_';
    let cpt = 0;
    (projet.tirs ?? []).forEach(tir => {
      if (tir.nom.startsWith(prefixe)) {
        const numero = Number(tir.nom.substring(prefixe.length)) || 0;
        if (numero > cpt) cpt = numero;
      }
    });
    return prefixe + (cpt + 1);
  }

  /** Équivalent de MonProjetComponent.onTirCree : recharge la liste et ouvre le nouveau blast. */
  private onBlastCreated(tir: Tir, fallbackProjetId: number): void {
    const projetId = tir.projetId ?? fallbackProjetId;
    this.siteNav.reloadBlasts();
    this.http.get<Projet>('Projects/' + projetId).subscribe({
      next: projet => {
        this.userService.currentProjet = projet;
        this.userService.currentTir = projet.tirs?.find(t => t.tirId === tir.tirId) ?? tir;
        this.navigateToBlast(tir, projetId);
      },
      error: () => this.navigateToBlast(tir, projetId),
    });
  }

  private navigateToBlast(tir: Tir, projetId: number): void {
    this.router.navigate(['/site', this.siteNav.siteId()], {
      queryParams: { projectId: projetId, blastId: tir.tirId, nodeType: 'blast', nodeValue: tir.nom },
    });
  }

  /** Reprise de TirInfoComponent.creerRapport. */
  openReport(blastId: number): void {
    const siteId = this.userService.currentSite()?.siteId;
    this.fonctionsService.loading_screen(true);

    forkJoin([
      this.http.get<Fichier[]>('Sites/' + siteId + '/Templates'),
      this.http.get<Tir>('Blasts/' + blastId),
    ]).subscribe({
      next: ([fichiers, tir]) => {
        this.fonctionsService.loading_screen(false);
        if (!tir) return;
        this.modalService.show(RapportCreationModaleComponent, {
          backdrop:           true,
          ignoreBackdropClick: true,
          keyboard:           false,
          class:              'modal-dialog-centered old-theme',
          initialState: {
            templates: signal(fichiers),
            tir:       signal(tir),
            siteId:    signal(siteId),
          } as Partial<RapportCreationModaleComponent>,
        });
      },
      error: () => this.fonctionsService.loading_screen(false),
    });
  }

  /** Reprise de TirInfoComponent.ouvrirModaleModif. */
  openModify(blastId: number): void {
    const siteId = this.userService.currentSite()?.siteId;
    this.fonctionsService.loading_screen(true);

    forkJoin([
      this.http.get<Tir>('Blasts/' + blastId),
      this.http.get<Zone[]>('Sites/' + siteId + '/Zones'),
      this.http.get<TypeTir[]>('Sites/' + siteId + '/BlastTypes'),
    ]).subscribe({
      next: ([tirActif, zones, typeTirs]) => {
        // TirModaleComponent lit currentProjet dès son constructeur : l'ancien front l'avait
        // déjà chargé via mon-projet, /new-site non.
        this.withCurrentProjet(tirActif.projetId, () => {
          this.fonctionsService.loading_screen(false);
          tirActif.dateTirPrevisionnelle = new Date(tirActif.dateTirPrevisionnelle);
          if (tirActif.dateTirEffective)
            tirActif.dateTirEffective = new Date(tirActif.dateTirEffective);

          const modalRef = this.modalService.show(TirModaleComponent, {
            backdrop:            true,
            ignoreBackdropClick: true,
            keyboard:            false,
            class:               'modal-lg modal-dialog-centered old-theme',
            animated:            false,
            initialState: {
              tabZones:           zones,
              tabTypeTirs:        typeTirs,
              zoneSelectionnee:   zones.find(o => o.zoneId == tirActif.zoneId),
              typeTirSelectionne: typeTirs.find(o => o.typeTirId == tirActif.typeTirId),
              estModif:           true,
              niveauTir:          tirActif.niveau,
              tir:                tirActif,
            } as Partial<TirModaleComponent>,
          });

          modalRef.content!.modifTir.subscribe(tir => {
            if (tir == null) {
              this.onBlastDeleted(tirActif);
            } else {
              this.onBlastModified(tir);
            }
          });
        });
      },
      error: () => this.fonctionsService.loading_screen(false),
    });
  }

  private withCurrentProjet(projetId: number, next: () => void): void {
    if (this.userService.currentProjet?.projetId === projetId) {
      next();
      return;
    }
    this.http.get<Projet>('Projects/' + projetId).subscribe({
      next: projet => {
        this.userService.currentProjet = projet;
        next();
      },
      error: () => this.fonctionsService.loading_screen(false),
    });
  }

  /** Équivalent de MonProjetComponent.tirModifie : met à jour le tir en mémoire et la liste. */
  private onBlastModified(tir: Tir): void {
    if (this.userService.currentTir?.tirId === tir.tirId) {
      Object.assign(this.userService.currentTir, tir);
    }
    this.siteNav.reloadBlasts();
  }

  /** Équivalent de TirInfoComponent (suppTir + retour au projet), sur la route /site. */
  private onBlastDeleted(tir: Tir): void {
    this.userService.currentTir = null;
    this.siteNav.reloadBlasts();
    this.router.navigate(['/site', this.siteNav.siteId()], {
      queryParams: { projectId: tir.projetId, nodeType: 'project', nodeValue: tir.nomProjet },
    });
  }
}
